// YERİ: src/app/core/i18n/portal-locale.ts
//
// Portal ağacının LOCALE_ID'sini üreten fabrika.
//
// Neden ayrı bir dosya: app.routes.ts uygulamanın İLK yüklenen modüllerinden
// biri. PortalThemeService'i oradan import etmek tüm tema motorunu (renk körlüğü
// filtreleri, font yükleyici, MutationObserver) vitrin ziyaretçisinin ilk
// paketine sokardı. Burada yalnız localStorage'dan bir string okunuyor.

import { PLATFORM_ID, inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';
import { DEFAULT_LOCALE, findLocale, registerAppLocale } from './locale';

/** PortalThemeService'in localStorage anahtarı — orada da aynı sabit var. */
const THEME_STORAGE_KEY = 'portal_theme_v1';

/**
 * Kullanıcının kayıtlı panel dilinin Angular locale kimliği ('tr', 'en-CA'…).
 *
 * Sunucuda localStorage YOK — SSR'da varsayılana düşer. Portal ağacı zaten
 * RenderMode.Client olduğu için bu yol pratikte sunucuda hiç çalışmaz, ama
 * guard yine de duruyor: bir gün portal SSR'a alınırsa sessizce patlamak
 * yerine varsayılanla çalışsın.
 *
 * DB'deki tercih (portal/preferences/theme) açılıştan SONRA geliyor; LOCALE_ID
 * o ana kadar çözülmüş oluyor. Bu yüzden yeni bir cihazda ilk oturumda biçimler
 * varsayılan kalır, kullanıcı dili seçtiği anda localStorage'a yazılır ve
 * sonraki açılışta doğru gelir.
 */
export function readStoredPortalLocale(): string {
  const isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  if (!isBrowser) return findLocale(DEFAULT_LOCALE).angularLocale;

  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    const code = raw ? (JSON.parse(raw) as { language?: string }).language : null;
    return findLocale(code).angularLocale;
  } catch {
    // Bozuk JSON tüm portalı açılmaz hâle getirmemeli.
    return findLocale(DEFAULT_LOCALE).angularLocale;
  }
}

/**
 * Portal açılmadan ÖNCE seçili dilin biçim verisini VE sözlüğünü yükler.
 *
 * Biçim verisi olmadan sistem şöyle kırılıyordu: LOCALE_ID 'fr' döner, ilk
 * `{{ tarih | date }}` çalışır ve Angular "Missing locale data for the locale
 * fr" diye HATA FIRLATIR — sayfa hiç çizilmez. Veri tembel yüklendiği için
 * (11 dili pakete koymamak adına) yüklenmesini beklemek zorundayız.
 *
 * Sözlük de AYNI sebeple burada bekletiliyor: yüklenmeden çizilirse kullanıcı
 * bir an için 'portal.nav.customers' gibi anahtar adları görür, sonra metinler
 * yerine oturur. Tek karelik bu titreme, panelin bozuk olduğu izlenimi veriyor.
 *
 * Her zaman true döner: hiçbiri inmese bile kullanıcıyı paneline sokmamak,
 * tarihlerin veya metinlerin varsayılan dilde görünmesinden çok daha kötü.
 */
export const localeReadyGuard: CanActivateFn = async () => {
  const isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  if (!isBrowser) return true;

  // `optional: true` ŞART. Bu bekçi rotanın `providers` dizisiyle aynı rotada
  // duruyor ve çözülme sırası Angular sürümüne göre değişebiliyor; servis o an
  // görünmezse ZORUNLU inject NullInjectorError fırlatır, guard patlar ve
  // PANEL HİÇ AÇILMAZ. Sözlük yüklenmemesi metinlerin Türkçe kalmasıdır;
  // kullanıcıyı kapıda bırakmakla kıyaslanamaz.
  const transloco = inject(TranslocoService, { optional: true });

  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    const code = raw ? (JSON.parse(raw) as { language?: string }).language : null;
    const locale = findLocale(code);

    await Promise.all([
      registerAppLocale(locale.code),
      // Sözlük inmezse (ağ hatası) panel yine açılmalı — bu yüzden yutuluyor.
      transloco ? firstValueFrom(transloco.load(locale.code)).catch(() => null) : null,
    ]);
  } catch { /* biçimler ve metinler varsayılan kalır */ }

  return true;
};
